import { createContext, useState, useEffect, useContext } from 'react';
import {JobDetailData} from "@spa/features/search_job/pc/models/JobDetailData";
import {useLocation} from "react-router-dom";
import {usePage} from "@inertiajs/react";
import {getQueryParam} from "@spa/utils/urlHelper";

const JobSwitchContext = createContext(null);

export const useJobSwitch = () => {
    return useContext(JobSwitchContext);
}

export const JobSwitchProvider = ({ children, searchResultViewModel, validatedUniqueId }) => {
    const { route } = usePage().props;
    const routerLocation = useLocation();

    /**
     * activeUniqueIdの初期値
     * jbパラメータと同期するが、jbパラメータは外部JSで付与するため初期値は参照しない
     * @see addUrlParameter.js
     */
    const initialActiveUniqueId = validatedUniqueId || searchResultViewModel.jobViewModelList[0].uniqueId;
    /**
     * 詳細表示されている求人のuniqueId
     * jbパラメータと同期する
     */
    const [activeUniqueId, setActiveUniqueId] = useState(initialActiveUniqueId);
    /**
     * jobViewModelForDetailの初期値
     */
    const initialJobViewModelForDetail = () => {
        // 求人詳細がない場合、詳細エラーとして表示
        if (searchResultViewModel.jobDetailViewModel === null) {
            return createDetail(null, null, route);
        }
        const jobViewModelFromSearchResult = searchResultViewModel.jobViewModelList.find((jobViewModel) => {
            return initialActiveUniqueId === jobViewModel.uniqueId;
        });
        return createDetail(searchResultViewModel.jobDetailViewModel, jobViewModelFromSearchResult, route);
    }
    /**
     * 詳細表示されている求人の詳細ViewModel
     */
    const [jobViewModelForDetail, setJobViewModelForDetail] = useState(initialJobViewModelForDetail);

    const [isFirstRender, setIsFirstRender] = useState(true);
    const [isJobDetailLoading, setIsJobDetailLoading] = useState(false);

    /**
     * URLの変更を監視して詳細表示を更新する
     */
    useEffect(() => {
        if (isFirstRender) {
            setIsFirstRender(false);
            return;
        }

        const uniqueIdInUrl = getQueryParam(routerLocation, 'jb');

        if (uniqueIdInUrl === activeUniqueId) {
            return;
        }

        setIsJobDetailLoading(true);
        setActiveUniqueId(uniqueIdInUrl);

        // 詳細表示を更新
        createJobViewModelForDetail(uniqueIdInUrl);
    }, [routerLocation]);

    /**
     * 求人詳細の共通部分以外を更新
     * @param {string} uniqueId
     * @returns {Promise<Object>}
     */
    const fetchJobDetail = async (uniqueId) => {
        const response = await fetch(`/api/jobs/${uniqueId}`);
        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return data.detail;
    }

    /**
     * 詳細表示のViewModelを作成
     * @param uniqueId
     * @return {Promise<void>}
     */
    const createJobViewModelForDetail = async (uniqueId) => {
        const jobViewModelFromSearchResult = searchResultViewModel.jobViewModelList.find((jobViewModel) => {
            return uniqueId === jobViewModel.uniqueId;
        });

        // 先に検索結果から詳細表示のViewModelを作成
        setJobViewModelForDetail(createDetail(null, jobViewModelFromSearchResult, route));

        const jobDetailViewModel = await fetchJobDetail(uniqueId);

        if (!jobDetailViewModel) {
            // 詳細エラーとして表示
            setJobViewModelForDetail(createDetail(null, null, route));
            setIsJobDetailLoading(false);
            return;
        }

        // APIから取得した詳細表示のViewModelを作成
        setJobViewModelForDetail(createDetail(jobDetailViewModel, jobViewModelFromSearchResult, route));
        setIsJobDetailLoading(false);
    }

    return (
        <JobSwitchContext.Provider value={{
            jobViewModelForDetail,
            isJobDetailLoading,
            isFirstRender,
            activeUniqueId,
        }}>
            {children}
        </JobSwitchContext.Provider>
    );
}

/**
 * @param jobDetailViewModel
 * @param jobViewModelFromSearchResult
 * @param route
 * @return {JobDetailData}
 */
const createDetail = (jobDetailViewModel, jobViewModelFromSearchResult, route) => {
    // 検索結果と詳細の両方がない場合
    if (!jobDetailViewModel && !jobViewModelFromSearchResult) {
        return null;
    }

    // 詳細がない場合
    if (!jobDetailViewModel) {
        return new JobDetailData({
            uniqueId: jobViewModelFromSearchResult.uniqueId,
            title: jobViewModelFromSearchResult.title,
            companyName: jobViewModelFromSearchResult.companyName,
            locations: jobViewModelFromSearchResult.locations,
            jobType: jobViewModelFromSearchResult.jobType,
            stationName: jobViewModelFromSearchResult.stationName,
            salary: jobViewModelFromSearchResult.salary + ' ' + jobViewModelFromSearchResult.salaryType,
            featureViewModel: jobViewModelFromSearchResult.featureViewModel,
            contentViewModel: null,
            sourceName: null,
            trainList: jobViewModelFromSearchResult.trainList,
            isDisplayCompanyName: jobViewModelFromSearchResult.isDisplayCompanyName,
            isDisplayLocation: jobViewModelFromSearchResult.isDisplayLocation,
            isDisplayJobType: jobViewModelFromSearchResult.isDisplayJobType,
            isDisplayStation: jobViewModelFromSearchResult.isDisplayStation,
            isDisplaySalary: jobViewModelFromSearchResult.isDisplaySalary,
            isDisplayFeatures: jobViewModelFromSearchResult.featureViewModel.isDisplayable,
            redirectUrl: route.web.redirect.replace(':uniqueId', jobViewModelFromSearchResult.uniqueId),
            jobcubeDetailUrl: route.web.detail.replace(':uniqueId', jobViewModelFromSearchResult.uniqueId),
            fromSearchResult: true,
            isValid: true,
            analyticsViewModel: null,
            companyNameExistence: jobViewModelFromSearchResult.analyticsData.companyNameExistence,
        });
    }

    // 検索結果がない場合
    if (!jobViewModelFromSearchResult) {
        const companyNameExistence = jobDetailViewModel.jobViewModel.companyName ? '1' : '0';
        return new JobDetailData({
            uniqueId: jobDetailViewModel.jobViewModel.uniqueId,
            title: jobDetailViewModel.jobViewModel.title,
            companyName: jobDetailViewModel.jobViewModel.companyName,
            locations: jobDetailViewModel.jobViewModel.locations,
            jobType: jobDetailViewModel.jobViewModel.jobTypes,
            stationName: jobDetailViewModel.jobViewModel.stationName,
            salary: jobDetailViewModel.jobViewModel.salary,
            featureViewModel: jobDetailViewModel.jobViewModel.featureViewModel,
            contentViewModel: jobDetailViewModel.jobViewModel.contentViewModel,
            sourceName: jobDetailViewModel.jobViewModel.sourceName,
            trainList: jobDetailViewModel.jobViewModel.trainList,
            isDisplayCompanyName: jobDetailViewModel.jobViewModel.isDisplayCompanyName,
            isDisplayLocation: jobDetailViewModel.jobViewModel.isDisplayLocation,
            isDisplayJobType: jobDetailViewModel.jobViewModel.isDisplayJobTypes,
            isDisplayStation: jobDetailViewModel.jobViewModel.isDisplayStation,
            isDisplaySalary: jobDetailViewModel.jobViewModel.isDisplaySalary,
            isDisplayFeatures: jobDetailViewModel.jobViewModel.isDisplayFeatures,
            redirectUrl: jobDetailViewModel.jobViewModel.redirectUrl,
            jobcubeDetailUrl: jobDetailViewModel.jobViewModel.jobcubeDetailUrl,
            fromSearchResult: false,
            isValid: jobDetailViewModel.jobViewModel.isValid,
            analyticsViewModel: jobDetailViewModel.analyticsViewModel,
            companyNameExistence: companyNameExistence
        });
    }

    // 検索結果と詳細がある場合
    return new JobDetailData({
        uniqueId: jobDetailViewModel.jobViewModel.uniqueId,
        title: jobViewModelFromSearchResult.title,
        companyName: jobViewModelFromSearchResult.companyName,
        locations: jobViewModelFromSearchResult.locations,
        jobType: jobViewModelFromSearchResult.jobType,
        stationName: jobViewModelFromSearchResult.stationName,
        salary: jobViewModelFromSearchResult.salary + ' ' + jobViewModelFromSearchResult.salaryType,
        featureViewModel: jobViewModelFromSearchResult.featureViewModel,
        contentViewModel: jobDetailViewModel.jobViewModel.contentViewModel,
        sourceName: jobDetailViewModel.jobViewModel.sourceName,
        trainList: jobViewModelFromSearchResult.trainList,
        isDisplayCompanyName: jobViewModelFromSearchResult.isDisplayCompanyName,
        isDisplayLocation: jobViewModelFromSearchResult.isDisplayLocation,
        isDisplayJobType: jobViewModelFromSearchResult.isDisplayJobType,
        isDisplayStation: jobViewModelFromSearchResult.isDisplayStation,
        isDisplaySalary: jobViewModelFromSearchResult.isDisplaySalary,
        isDisplayFeatures: jobViewModelFromSearchResult.featureViewModel.isDisplayable,
        redirectUrl: jobDetailViewModel.jobViewModel.redirectUrl,
        jobcubeDetailUrl: jobDetailViewModel.jobViewModel.jobcubeDetailUrl,
        fromSearchResult: true,
        isValid: jobDetailViewModel.jobViewModel.isValid,
        analyticsViewModel: jobDetailViewModel.analyticsViewModel,
        companyNameExistence: jobViewModelFromSearchResult.analyticsData.companyNameExistence,
    });
}
